import type { LiveDrone } from '@/hooks/useWebSocket';
import type { ManualControlHold } from '@/types/coordination';
import { manualHoldMark, manualHoldTag } from '@/lib/manual-hold';
import {
  RANGE_RING_RADII_M,
  RANGE_RING_STEPS,
  circleRingCoords,
  estimateTransit,
  formatRingLabel,
  formatTransitLabel,
  ringLabelPoint,
} from '@/lib/range-rings';
import { EMPTY_MANUAL_HOLDS, EMPTY_TRIGGERS, STATUS_COLORS } from './droneMapTypes';
import {
  EMPTY_HANDOVER_LINKS,
  handoverLabel,
  handoverNeedsAttention,
  isAirborne,
  type HandoverAttention,
  type HandoverLink,
} from './handoverLink';
import type { CooperativeTraffic, GeofenceOverlay, RcController, SiteMarker, DroneMapMission, DockMarker, PlanRoutePoint, TriggerMarker } from './droneMapTypes';

/**
 * Resolve a dock's live status to one of four visual states. This is the single
 * source of truth for BOTH the map glyph variant (dock-<state> icon) and its
 * colour, so the shape and the colour can never disagree about a dock's state.
 * An offline dock (is_online=false) reads offline even if its last status was
 * 'ready' - matching how the fleet table and command bar treat it.
 */
export type DockSymbolState = 'ready' | 'busy' | 'offline' | 'emergency';

export const DOCK_STATE_COLORS: Record<DockSymbolState, string> = {
  ready: '#22C55E',
  busy: '#F59E0B',
  offline: '#52525B',
  emergency: '#EF4444',
};

export function dockSymbolState(status: string, isOnline: boolean): DockSymbolState {
  if (status === 'emergency') return 'emergency';
  if (status === 'offline' || !isOnline) return 'offline';
  if (status === 'ready') return 'ready';
  return 'busy';
}

/** 0=fresh, 1=stale 30–60s (amber pulse), 2=lost >60s (gray + LOST label) */
export function staleLevel(lastSeenAt: string | undefined, now: number): 0 | 1 | 2 {
  const t = lastSeenAt ? new Date(lastSeenAt).getTime() : 0;
  const age = now - t;
  if (age > 60000) return 2;
  if (age > 30000) return 1;
  return 0;
}

function getAltitudeBand(altitude: number): string {
  if (altitude <= 50) return 'low';
  if (altitude <= 100) return 'mid';
  if (altitude <= 200) return 'high';
  return 'extreme';
}

function dronesToGeoJson(
  drones: LiveDrone[],
  now: number,
  selectedDroneId: string | null,
  manualHolds: ReadonlyMap<string, ManualControlHold>,
): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: drones.map((d) => {
      const level = staleLevel(d.last_seen_at, now);
      const baseColor = STATUS_COLORS[d.status] || STATUS_COLORS.idle;
      const color = level === 2 ? STATUS_COLORS.offline : baseColor;
      // Evaluated against the SAME `now` the staleness above uses, so the mark
      // ages out on the map's own 1 Hz tick rather than waiting for a telemetry
      // frame the held aircraft may have stopped sending.
      const manual = manualHoldMark(manualHolds.get(d.drone_id), now);
      return {
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: [d.longitude, d.latitude] },
        properties: {
          id: d.drone_id,
          callsign: d.callsign || d.drone_id.slice(-6).toUpperCase(),
          status: d.status || 'idle',
          altitude: d.altitude,
          speed: d.speed,
          heading: d.heading,
          battery: d.battery_percent,
          signal: d.signal_strength,
          color,
          stale_level: level,
          altitude_band: level === 2 ? 'lost' : getAltitudeBand(d.altitude || 0),
          altitude_int: Math.round(d.altitude || 0),
          // Drives the filtered 'drone-selection-bracket' layer. Kept true even
          // at stale_level 2 ("· LOST"): an operator still has that aircraft
          // selected, and the marker's own colour already reports staleness.
          selected: d.drone_id === selectedDroneId,
          // Somebody has their hands on this aircraft. Drives the filtered
          // 'drone-manual-ring' and 'drone-manual-tag' layers — the same
          // carried-in-the-data pattern as `selected` above, and for the same
          // reason: the mark is written by the call that moves the marker, so
          // it can never lag behind the aircraft it belongs to.
          manual_hold: manual !== null,
          // `MANUAL` alone when the broadcast carried no name. A hold with no
          // name is still a hold — see ManualControlStatePayload.operator.
          manual_label: manual ? manualHoldTag(manual) : '',
        },
      };
    }),
  };
}

function controllersToGeoJson(controllers: RcController[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: controllers.map((c) => ({
      type: 'Feature' as const,
      geometry: { type: 'Point' as const, coordinates: [c.longitude, c.latitude] },
      properties: { drone_id: c.drone_id },
    })),
  };
}

function buildHeadingLineFeatures(drones: LiveDrone[], now: number): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  for (const d of drones) {
    if (staleLevel(d.last_seen_at, now) === 2) continue;
    if (!d.speed || d.speed <= 0) continue;
    const headingRad = (d.heading * Math.PI) / 180;
    const latRad = (d.latitude * Math.PI) / 180;
    const distMeters = d.speed * 8;
    const endLat = d.latitude + (distMeters / 111320) * Math.cos(headingRad);
    const endLng = d.longitude + (distMeters / 111320) * Math.sin(headingRad) / Math.cos(latRad);
    features.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: [[d.longitude, d.latitude], [endLng, endLat]] },
      properties: { drone_id: d.drone_id, color: STATUS_COLORS[d.status] || STATUS_COLORS.idle },
    });
  }
  return { type: 'FeatureCollection', features };
}

/**
 * The aircraft the range rings and transit line are anchored to, or null.
 *
 * Three conditions, all of them about honesty rather than taste:
 * - the operator has PICKED it (rings are advice about one aircraft, never a
 *   fleet-wide wash of ink);
 * - it is AIRBORNE (`status === 'flying'`, the same signal the strip board and
 *   the FLYING counter use) - rings around a parked aircraft answer nothing;
 * - its link is not LOST (stale_level 2). Rings drawn around a position the
 *   platform stopped believing 60 seconds ago would put a 5 km circle of
 *   authority on a guess.
 */
export function rangeRingAnchor(
  drones: LiveDrone[],
  selectedDroneId: string | null,
  now: number,
): LiveDrone | null {
  if (!selectedDroneId) return null;
  const drone = drones.find((d) => d.drone_id === selectedDroneId);
  if (!drone || drone.status !== 'flying') return null;
  if (staleLevel(drone.last_seen_at, now) === 2) return null;
  return drone;
}

/**
 * Colour of a mission's route, by status.
 *
 * Drafts are previews, not commitments — gray, matching the offline/idle
 * register everywhere else, so a route no aircraft will fly unprompted can
 * never read as a scheduled one.
 *
 * Exported because the COP is not the only surface that draws a route: the
 * mission record page and the printable briefing draw the same one, and a
 * second palette is how a mission ends up cyan on one screen and blue on
 * another.
 */
export function missionRouteColor(status: string): string {
  if (status === 'in_progress') return '#22C55E';
  if (status === 'draft') return '#6B7280';
  return '#06B6D4';
}

export interface MissionFeatureSets {
  routes: GeoJSON.Feature[];
  waypoints: GeoJSON.Feature[];
  /**
   * 022/#700. One ring per ORBIT that has a usable radius, carrying the same salience flags as
   * the route lines so a selected orbit brightens and the rest dim with everything else.
   */
  orbits: GeoJSON.Feature[];
}

/**
 * Route lines and numbered waypoints for a set of missions, with the salience
 * flags the layers' paint expressions read.
 *
 * `selectedMissionId` / `hoveredMissionId` are the COP's concern; a static
 * single-route map passes null for both and gets the plain register.
 */
export function buildMissionFeatures(
  missions: ReadonlyArray<DroneMapMission>,
  selectedMissionId: string | null,
  hoveredMissionId: string | null,
): MissionFeatureSets {
  const routes: GeoJSON.Feature[] = [];
  const waypoints: GeoJSON.Feature[] = [];
  const orbits: GeoJSON.Feature[] = [];
  for (const m of missions) {
    const lineColor = missionRouteColor(m.status);
    const selected = m.id === selectedMissionId;
    const hovered = m.id === hoveredMissionId;
    // Dim only when the operator has picked a mission: at rest every route
    // keeps its normal salience, and hover alone brightens without punishing
    // the rest of the picture.
    const dim = selectedMissionId !== null && !selected && !hovered;
    if (m.waypoints.length >= 2) {
      routes.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: m.waypoints.map((w) => [w.longitude, w.latitude]) },
        properties: { id: m.id, name: m.name, status: m.status, color: lineColor, selected, hovered, dim },
      });
    }
    // THE CIRCLE AN ORBIT IS FLYING, on the live picture and not only in compose (#700 review).
    //
    // An orbit stores exactly one waypoint, so the `>= 2` route line above never fires and the
    // loop below drew a single dot at the centre. Everything else on this map - the aircraft
    // track, the geofences, the other routes - was drawn to scale, and the one mission type
    // whose parameters ARE its flight path contributed a point. An operator deconflicting a
    // 500 m circle against a boundary had nothing to look at.
    //
    // Same guard as the compose ring: exactly one waypoint AND a usable radius. More than one
    // waypoint means the row and the type disagree about what this mission is, and drawing a
    // ring around waypoint one would hide that rather than show it.
    if (m.type === 'orbit' && m.orbit_radius_m != null
      && Number.isFinite(m.orbit_radius_m) && m.orbit_radius_m > 0
      && m.waypoints.length === 1) {
      const centre = m.waypoints[0];
      orbits.push({
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [circleRingCoords(centre.latitude, centre.longitude, m.orbit_radius_m)],
        },
        properties: {
          id: m.id,
          name: m.name,
          status: m.status,
          color: lineColor,
          radius_m: m.orbit_radius_m,
          direction: m.orbit_direction ?? null,
          selected,
          hovered,
          dim,
        },
      });
    }
    for (const w of m.waypoints) {
      waypoints.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [w.longitude, w.latitude] },
        properties: {
          mission_id: m.id,
          // Carried so a waypoint can be filtered by its mission's status the
          // same way its route line is - without it, turning mission routes off
          // would leave a flying aircraft's route drawn with no numbers on it.
          status: m.status,
          sequence_order: w.sequence_order,
          altitude: w.altitude,
          color: lineColor,
          selected,
          hovered,
          dim,
        },
      });
    }
  }
  return { routes, waypoints, orbits };
}

/** First waypoint of a mission by sequence order, or null for an empty route. */
function firstWaypoint(mission: DroneMapMission): DroneMapMission['waypoints'][number] | null {
  let first: DroneMapMission['waypoints'][number] | null = null;
  for (const w of mission.waypoints) {
    if (!first || w.sequence_order < first.sequence_order) first = w;
  }
  return first;
}

/**
 * Concentric range rings around the anchor aircraft, plus one caption per ring.
 *
 * Lines and label points share a source and are split by geometry-type in the
 * layers - the same shape `zone-draw-preview` already uses.
 *
 * NOTE what is NOT here: a battery-derived reach ring. `batteryReachRadiusM`
 * explains why in one place (no endurance model exists to derive it from), and
 * the map legend states it to the operator rather than leaving a gap that reads
 * as unlimited range.
 */
function buildRangeRingFeatures(anchor: LiveDrone | null): GeoJSON.Feature[] {
  if (!anchor) return [];
  const features: GeoJSON.Feature[] = [];
  for (const radiusM of RANGE_RING_RADII_M) {
    const label = formatRingLabel(radiusM);
    features.push({
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: circleRingCoords(anchor.latitude, anchor.longitude, radiusM, RANGE_RING_STEPS),
      },
      properties: { radius_m: radiusM, label },
    });
    features.push({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: ringLabelPoint(anchor.latitude, anchor.longitude, radiusM),
      },
      properties: { radius_m: radiusM, label },
    });
  }
  return features;
}

/**
 * Advisory leg from the anchor aircraft to the first waypoint of the mission the
 * operator is pointing at: a dashed line plus a caption carrying distance, time
 * and the speed the time assumes.
 *
 * Hover wins over selection because hover is the live question ("what would it
 * cost to send this aircraft there?") while the selection is the answer already
 * being read.
 *
 * Suppressed when the mission is the one this aircraft is already flying - a leg
 * to the start of your own route is not advice, it is clutter pointing backwards.
 */
function buildTransitFeatures(
  anchor: LiveDrone | null,
  missions: DroneMapMission[],
  selectedMissionId: string | null,
  hoveredMissionId: string | null,
): GeoJSON.Feature[] {
  if (!anchor) return [];
  const targetId = hoveredMissionId ?? selectedMissionId;
  if (!targetId) return [];
  const mission = missions.find((m) => m.id === targetId);
  if (!mission) return [];
  if (mission.assigned_drone_id && mission.assigned_drone_id === anchor.drone_id) return [];
  const wp = firstWaypoint(mission);
  if (!wp) return [];

  const from = { lat: anchor.latitude, lng: anchor.longitude };
  const to = { lat: wp.latitude, lng: wp.longitude };
  const estimate = estimateTransit(from, to, anchor.speed);
  const label = formatTransitLabel(estimate);
  return [
    {
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: [[from.lng, from.lat], [to.lng, to.lat]] },
      properties: { mission_id: mission.id, label, distance_m: estimate.distanceM, duration_s: estimate.durationS },
    },
    {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [(from.lng + to.lng) / 2, (from.lat + to.lat) / 2],
      },
      properties: { mission_id: mission.id, label },
    },
  ];
}

/** Collect drone_ids with stale_level === 1 in a single pass */
export function collectStalePulseIds(drones: LiveDrone[], now: number): Set<string> {
  const pulseIds = new Set<string>();
  for (const d of drones) {
    if (staleLevel(d.last_seen_at, now) === 1) pulseIds.add(d.drone_id);
  }
  return pulseIds;
}

export interface UpdateSourcesParams {
  drones: LiveDrone[];
  geofences: GeofenceOverlay[];
  cooperativeTraffic: CooperativeTraffic[];
  controllers: RcController[];
  sites: SiteMarker[];
  missions: DroneMapMission[];
  docks: DockMarker[];
  /** Optional with a stable default, like `manualHolds`: a picture with no
   *  unanswered triggers is the normal state, not a missing input. */
  triggers?: TriggerMarker[];
  /**
   * The chosen drone/dock, stamped onto the features themselves as `selected`
   * so the selection-bracket layers can filter on it.
   *
   * Deliberately carried IN the feature data rather than via
   * `map.setFeatureState()`, which is the pattern MapLibre and Mapbox normally
   * recommend for exactly this. Two concrete reasons it does not fit here:
   *
   * 1. `feature-state` expressions are not permitted in `filter` (or in any
   *    layout property) - only in paint. The bracket is a filtered symbol
   *    layer, so keying it on feature state is not expressible; it would have
   *    to become an `icon-opacity` trick that still lays out an icon for every
   *    drone on the map.
   * 2. These features carry no top-level GeoJSON `id`, only `properties.id`,
   *    so feature state would additionally need `promoteId: 'id'` on both
   *    sources plus an explicit `removeFeatureState` on every deselect.
   *
   * (Note it is NOT because state is lost on `setData()` - in maplibre-gl 5.x
   * feature state lives on the tile manager and is re-applied to reloaded
   * tiles, so it does survive. Correcting that here because the wrong reason
   * would steer the next person away from `setFeatureState` map-wide.)
   *
   * The upside of carrying it in the data: the bracket is written by the same
   * call that moves the marker, so it can never lag or desync from it.
   */
  selectedDroneId: string | null;
  selectedDockId: string | null;
  /**
   * Who is hand-flying what, org-wide, exactly as `useWebSocket` keeps it.
   *
   * The RAW holds rather than a pre-resolved list of ids, because whether a
   * hold still counts is a question about the clock: it is answered here
   * against the same `now` the staleness bands use, on the map's own 1 Hz tick.
   * Resolving it upstream would freeze the answer at whatever the caller last
   * re-rendered for, which for a held aircraft that has stopped reporting is
   * exactly when the mark must expire.
   *
   * Optional: a map rendered without it (the manual minimap, tests) simply
   * marks nothing.
   */
  manualHolds?: ReadonlyMap<string, ManualControlHold>;
  /**
   * Mission salience, same in-the-data pattern as `selectedDroneId` above (and
   * for the same reasons): each mission feature is stamped `selected` /
   * `hovered` / `dim`, and the mission layers' paint expressions read those.
   * With a selection active every OTHER route dims, so the picked mission
   * stands out without any route leaving the picture.
   */
  selectedMissionId?: string | null;
  hoveredMissionId?: string | null;
  /**
   * Coverage handovers in progress. Resolved against the live drones and `now` HERE, on the map's
   * own 1 Hz tick, for the same reason `manualHolds` is: the countdown is a question about the
   * clock, and the line has to come down the tick an aircraft's link goes LOST.
   */
  handoverLinks?: HandoverLink[];
  /**
   * Operator toggle for the range/transit advisory overlay. Gated in the DATA
   * rather than by layer visibility (which is how the mission layers do it),
   * because these features exist only for a picked, airborne aircraft anyway:
   * with the toggle off the sources are simply empty, so there is one place that
   * decides whether the advice is on screen instead of two that can disagree.
   */
  showRangeRings?: boolean;
  trailDroneIds: Set<string>;
  controllerLinkId: string | null;
  zoneDrawMode: 'circle' | 'polygon' | null | undefined;
  zoneDrawPoints: [number, number][];
  zonePolygonClosed: boolean;
  zoneCircleCenter: { lat: number; lng: number } | null | undefined;
  zoneCircleRadius: number;
  /**
   * Colour the in-progress zone is drawn in — already resolved (operator pick,
   * else the zone type's default). Carried per feature rather than baked into
   * the layer paint so a colour change is a plain setData(), the same route the
   * geometry already takes; there is no edit path for a saved zone, so the
   * preview is the operator's only chance to see the colour before committing.
   */
  zoneDrawColor: string;
  planWaypoints: PlanRoutePoint[];
  /**
   * 022. Radius of the circle an ORBIT will fly around its single placed point, in metres.
   * Null/undefined for every other mission type, and for an orbit whose radius box does not
   * currently hold a usable number — a ring drawn at a guessed radius would be a lie about
   * where the aircraft is going.
   */
  planOrbitRadiusM: number | null | undefined;
  planHome: { lat: number; lng: number } | null | undefined;
  planSelectedIndex: number | null | undefined;
  /** Active operator-waypoint index from telemetry mission_current (live progress). */
  planActiveIndex: number | null | undefined;
  trailHistory: Map<string, [number, number][]>;
  autoTrailHistory: Map<string, [number, number][]>;
  now: number;
}

/** Draft plan overlay: numbered waypoint markers + home marker. */
function buildPlanWaypointFeatures(
  planWaypoints: PlanRoutePoint[],
  planHome: { lat: number; lng: number } | null | undefined,
  planSelectedIndex: number | null | undefined,
  planActiveIndex: number | null | undefined,
): GeoJSON.Feature[] {
  if (planWaypoints.length === 0 && !planHome) return [];
  const active = typeof planActiveIndex === 'number' ? planActiveIndex : null;
  const features: GeoJSON.Feature[] = [];
  if (planHome && planWaypoints.length > 0) {
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [planHome.lng, planHome.lat] },
      properties: { kind: 'home', label: 'H', invalid: false, selected: false, reached: false, active: false },
    });
  }
  planWaypoints.forEach((w, i) => {
    // Live progress: waypoints before the active target are reached, the active
    // index is the current target, the rest are pending. Null active = no mission
    // executing, so nothing is reached/active.
    const reached = active !== null && i < active;
    const isActive = active !== null && i === active;
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [w.lng, w.lat] },
      properties: {
        kind: 'wp',
        index: i,
        label: String(i + 1),
        invalid: w.invalid,
        selected: i === planSelectedIndex,
        reached,
        active: isActive,
      },
    });
  });
  return features;
}

/**
 * Live progress overlay: the currently-active leg of the planned polyline -
 * the segment from the last-reached waypoint (or home for the first leg) to the
 * current target waypoint. Empty unless a mission is executing (planActiveIndex
 * is a valid index into planWaypoints).
 */
function buildPlanActiveLegFeatures(
  planWaypoints: PlanRoutePoint[],
  planHome: { lat: number; lng: number } | null | undefined,
  planActiveIndex: number | null | undefined,
): GeoJSON.Feature[] {
  if (
    planActiveIndex === null ||
    planActiveIndex === undefined ||
    planActiveIndex < 0 ||
    planActiveIndex >= planWaypoints.length
  ) {
    return [];
  }
  const target = planWaypoints[planActiveIndex];
  // Leg origin is the previous waypoint, or home for the very first leg.
  const origin =
    planActiveIndex > 0
      ? { lng: planWaypoints[planActiveIndex - 1].lng, lat: planWaypoints[planActiveIndex - 1].lat }
      : planHome
        ? { lng: planHome.lng, lat: planHome.lat }
        : null;
  if (!origin) return [];
  return [{
    type: 'Feature',
    geometry: {
      type: 'LineString',
      coordinates: [[origin.lng, origin.lat], [target.lng, target.lat]],
    },
    properties: { active_index: planActiveIndex },
  }];
}

/**
 * The coverage handover line (#251): relief to outgoing, with the countdown at its midpoint.
 *
 * The rotation row is a claim about two aircraft; the live picture is the evidence. So a line is
 * drawn only while BOTH are on the picture, neither link is LOST, and the aircraft being relieved
 * is actually airborne - a row that outlives the flight must not join two parked airframes. The
 * relief may still be on its pad: that is the first minute of every handover.
 *
 * A normal handover gets the countdown (or `--:--`). One that needs attention (D17) gets no label, a
 * red line, and a ring on each aircraft - the ring, because in the last seconds the two are stacked
 * over one station and the line between them is a few pixels under two icons.
 */
function buildHandoverLinkFeatures(
  links: HandoverLink[],
  droneById: ReadonlyMap<string, LiveDrone>,
  manualHolds: ReadonlyMap<string, ManualControlHold>,
  now: number,
): GeoJSON.Feature[] {
  const features: GeoJSON.Feature[] = [];
  for (const link of links) {
    const relief = droneById.get(link.reliefDroneId);
    const outgoing = droneById.get(link.outgoingDroneId);
    if (!relief || !outgoing) continue;
    if (staleLevel(relief.last_seen_at, now) === 2 || staleLevel(outgoing.last_seen_at, now) === 2) continue;
    if (!isAirborne(outgoing)) continue;

    // The same freshness rule the MANUAL tag is painted by, so a ring and a tag cannot disagree.
    const manualHold = manualHoldMark(manualHolds.get(relief.drone_id), now) !== null
      || manualHoldMark(manualHolds.get(outgoing.drone_id), now) !== null;
    const attention: HandoverAttention = handoverNeedsAttention(link, relief, now, manualHold) ? 'attention' : 'none';

    const from: [number, number] = [relief.longitude, relief.latitude];
    const to: [number, number] = [outgoing.longitude, outgoing.latitude];
    features.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: [from, to] },
      properties: { action_id: link.actionId, attention },
    });

    if (attention === 'attention') {
      for (const at of [from, to]) {
        features.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: at },
          properties: { action_id: link.actionId, kind: 'attention-ring' },
        });
      }
      continue;
    }

    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [(from[0] + to[0]) / 2, (from[1] + to[1]) / 2] },
      properties: { action_id: link.actionId, kind: 'label', label: handoverLabel(link, relief, outgoing, now) },
    });
  }
  return features;
}

/** Polyline from the dock (when known) through every draft waypoint in order. */
function buildPlanRouteFeatures(
  planWaypoints: PlanRoutePoint[],
  planHome: { lat: number; lng: number } | null | undefined,
): GeoJSON.Feature[] {
  const coords: [number, number][] = [];
  if (planHome && planWaypoints.length > 0) coords.push([planHome.lng, planHome.lat]);
  for (const w of planWaypoints) coords.push([w.lng, w.lat]);
  if (coords.length < 2) return [];
  return [{
    type: 'Feature',
    geometry: { type: 'LineString', coordinates: coords },
    properties: {},
  }];
}

export function buildSourceUpdates(params: UpdateSourcesParams): Record<string, GeoJSON.FeatureCollection> {
  const {
    drones,
    geofences,
    cooperativeTraffic,
    controllers,
    sites,
    missions,
    docks,
    triggers = EMPTY_TRIGGERS,
    selectedDroneId,
    selectedDockId,
    manualHolds = EMPTY_MANUAL_HOLDS,
    selectedMissionId = null,
    hoveredMissionId = null,
    handoverLinks = EMPTY_HANDOVER_LINKS,
    showRangeRings = false,
    trailDroneIds,
    controllerLinkId,
    zoneDrawMode,
    zoneDrawPoints,
    zonePolygonClosed,
    zoneCircleCenter,
    zoneCircleRadius,
    zoneDrawColor,
    planWaypoints,
    planOrbitRadiusM,
    planHome,
    planSelectedIndex,
    planActiveIndex,
    trailHistory,
    autoTrailHistory,
    now,
  } = params;

  const droneById = new Map(drones.map((d) => [d.drone_id, d]));
  const controllerByDroneId = new Map(controllers.map((c) => [c.drone_id, c]));

  const autoTrailFeatures: GeoJSON.Feature[] = [];
  for (const d of drones) {
    const coords = autoTrailHistory.get(d.drone_id);
    if (coords && coords.length > 1) {
      autoTrailFeatures.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: coords },
        properties: { drone_id: d.drone_id, color: STATUS_COLORS[d.status] || STATUS_COLORS.idle },
      });
    }
  }

  const trailFeatures: GeoJSON.Feature[] = [];
  for (const droneId of trailDroneIds) {
    const coords = trailHistory.get(droneId);
    if (coords && coords.length > 1) {
      const drone = droneById.get(droneId);
      trailFeatures.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: coords },
        properties: { drone_id: droneId, color: STATUS_COLORS[drone?.status || 'idle'] || '#3B82F6' },
      });
    }
  }

  const { routes: missionRouteFeatures, waypoints: missionWpFeatures, orbits: missionOrbitFeatures } =
    buildMissionFeatures(missions, selectedMissionId, hoveredMissionId);

  const rangeAnchor = showRangeRings ? rangeRingAnchor(drones, selectedDroneId, now) : null;

  let controllerLink: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };
  if (controllerLinkId) {
    const drone = droneById.get(controllerLinkId);
    const ctrl = controllerByDroneId.get(controllerLinkId);
    if (drone && ctrl) {
      controllerLink = {
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: [[ctrl.longitude, ctrl.latitude], [drone.longitude, drone.latitude]] },
          properties: {},
        }],
      };
    }
  }

  // Every preview feature carries the colour, including the vertex points: the
  // fill, the outline and the vertices all read as one shape to the operator,
  // and a vertex left on a stale colour would look like a second, unrelated
  // object sitting on the zone being drawn.
  const drawPreviewFeatures: GeoJSON.Feature[] = [];
  if (zoneDrawMode === 'circle' && zoneCircleCenter && zoneCircleRadius > 0) {
    drawPreviewFeatures.push({
      type: 'Feature',
      properties: { color: zoneDrawColor },
      geometry: {
        type: 'Polygon',
        // Same ring math the range rings use, so a drawn zone and a range ring
        // of equal radius can never disagree about where their edge is.
        coordinates: [circleRingCoords(zoneCircleCenter.lat, zoneCircleCenter.lng, zoneCircleRadius)],
      },
    });
  } else if (zoneDrawMode === 'polygon' && zoneDrawPoints.length >= 2) {
    if (zoneDrawPoints.length >= 3 && zonePolygonClosed) {
      drawPreviewFeatures.push({
        type: 'Feature',
        properties: { color: zoneDrawColor },
        geometry: { type: 'Polygon', coordinates: [[...zoneDrawPoints, zoneDrawPoints[0]]] },
      });
    } else {
      drawPreviewFeatures.push({
        type: 'Feature',
        properties: { color: zoneDrawColor },
        geometry: { type: 'LineString', coordinates: zoneDrawPoints },
      });
    }
  }
  if (zoneDrawMode === 'polygon') {
    for (const pt of zoneDrawPoints) {
      drawPreviewFeatures.push({
        type: 'Feature',
        properties: { color: zoneDrawColor },
        geometry: { type: 'Point', coordinates: pt },
      });
    }
  }

  return {
    drones: dronesToGeoJson(drones, now, selectedDroneId, manualHolds),
    'drone-heading-lines': buildHeadingLineFeatures(drones, now),
    'drone-auto-trails': { type: 'FeatureCollection', features: autoTrailFeatures },
    docks: docks.length > 0
      ? {
          type: 'FeatureCollection',
          features: docks.map((d) => {
            const state = dockSymbolState(d.status, d.is_online);
            return {
              type: 'Feature' as const,
              geometry: { type: 'Point' as const, coordinates: [d.longitude, d.latitude] },
              properties: {
                id: d.id,
                callsign: d.callsign ?? d.id.slice(-6).toUpperCase(),
                status: d.status,
                is_online: d.is_online,
                // symbol_state picks the glyph variant; color tints the label
                // and hit debug - both derived from the one resolver above.
                symbol_state: state,
                color: DOCK_STATE_COLORS[state],
                // Drives the filtered 'dock-selection-bracket' layer.
                selected: d.id === selectedDockId,
              },
            };
          }),
        }
      : { type: 'FeatureCollection', features: [] },
    // One point per configured safe land zone, labelled with its dock. Drawn
    // under the docks: it is advice about where a fallback lands, never a
    // click target.
    'dock-safe-land': {
      type: 'FeatureCollection',
      features: docks.flatMap((d) => (d.safe_land
        ? [{
            type: 'Feature' as const,
            geometry: { type: 'Point' as const, coordinates: [d.safe_land.longitude, d.safe_land.latitude] },
            properties: {
              dock_id: d.id,
              label: `SAFE LAND ${d.callsign ?? d.id.slice(-6).toUpperCase()}`,
            },
          }]
        : [])),
    },
    // Unanswered external triggers. The score rides in the properties so the
    // label can carry it without a second lookup, and the id so a click can
    // resolve back to the row.
    //
    // A STANDING PROPOSAL SAYS SO ON THE PICTURE (review, 2026-09-12). The platform naming an
    // aircraft and starting a ten-minute clock used to look exactly like a detection nobody had
    // decided anything about: no alert is raised for a proposal and there is no banner or
    // counter anywhere on the COP, so the only way to find out the platform was waiting on a
    // human was to click each red point in turn - and if nobody did, the offer lapsed silently
    // and the operator was never told it had been made. That fails the product's own bar: the
    // situation has to be readable in under three seconds.
    //
    // Carried in the LABEL rather than as a colour, because colour alone is not a carrier
    // (`frontend-a11y-motion-contrast-wcag22`) and the text costs nothing - the field is
    // already on the row. `full_auto` gets no marker text: it has already flown, there is
    // nothing for anyone to answer, and the aircraft itself is about to appear on the map.
    triggers: {
      type: 'FeatureCollection',
      features: triggers.map((t) => {
        const base = t.score != null ? `PERSON ${Math.round(t.score)}%` : 'PERSON';
        const awaiting = t.proposal_mode === 'propose' && t.proposed_drone_id != null;
        return {
          type: 'Feature' as const,
          geometry: { type: 'Point' as const, coordinates: [t.longitude, t.latitude] },
          properties: {
            trigger_id: t.id,
            label: awaiting ? `${base} · AWAITING APPROVAL` : base,
            awaiting_approval: awaiting,
          },
        };
      }),
    },
    sites: sites.length > 0
      ? {
          type: 'FeatureCollection',
          features: sites.map((st) => ({
            type: 'Feature' as const,
            geometry: { type: 'Point' as const, coordinates: [st.longitude, st.latitude] },
            properties: {
              id: st.id,
              name: st.name,
              address: st.address || '',
              latitude: st.latitude,
              longitude: st.longitude,
              radius_meters: st.radius_meters,
              is_active: st.is_active !== false,
            },
          })),
        }
      : { type: 'FeatureCollection', features: [] },
    controllers: controllers.length > 0 ? controllersToGeoJson(controllers) : { type: 'FeatureCollection', features: [] },
    geofences: {
      type: 'FeatureCollection',
      features: geofences.map((g) => ({
        type: 'Feature' as const,
        geometry: g.geometry,
        properties: { id: g.id, name: g.name, type: g.type, color: g.color },
      })),
    },
    traffic: cooperativeTraffic.length > 0
      ? {
          type: 'FeatureCollection',
          features: cooperativeTraffic.map((t) => ({
            type: 'Feature' as const,
            geometry: { type: 'Point' as const, coordinates: [t.longitude, t.latitude] },
            properties: { id: t.id, callsign: t.callsign, type: t.type, altitude: t.altitude, heading: t.heading ?? 0 },
          })),
        }
      : { type: 'FeatureCollection', features: [] },
    trails: { type: 'FeatureCollection', features: trailFeatures },
    'mission-routes': { type: 'FeatureCollection', features: missionRouteFeatures },
    'mission-orbits': { type: 'FeatureCollection', features: missionOrbitFeatures },
    'mission-waypoints': { type: 'FeatureCollection', features: missionWpFeatures },
    'range-rings': { type: 'FeatureCollection', features: buildRangeRingFeatures(rangeAnchor) },
    'transit-line': {
      type: 'FeatureCollection',
      features: buildTransitFeatures(rangeAnchor, missions, selectedMissionId, hoveredMissionId),
    },
    'handover-link': {
      type: 'FeatureCollection',
      features: buildHandoverLinkFeatures(handoverLinks, droneById, manualHolds, now),
    },
    'controller-link': controllerLink,
    'zone-draw-preview': { type: 'FeatureCollection', features: drawPreviewFeatures },
    'plan-route': { type: 'FeatureCollection', features: buildPlanRouteFeatures(planWaypoints, planHome) },
    'plan-active-leg': {
      type: 'FeatureCollection',
      features: buildPlanActiveLegFeatures(planWaypoints, planHome, planActiveIndex),
    },
    'plan-waypoints': {
      type: 'FeatureCollection',
      features: buildPlanWaypointFeatures(planWaypoints, planHome, planSelectedIndex, planActiveIndex),
    },
    'plan-orbit': {
      type: 'FeatureCollection',
      features: buildPlanOrbitFeatures(planWaypoints, planOrbitRadiusM),
    },
  };
}

/**
 * The ring an ORBIT will fly (022), around the one point the operator placed.
 *
 * Uses `circleRingCoords` — the same ring maths as the range rings and the circle geofence
 * preview — so a 500 m orbit and a 500 m zone can never disagree about where their edge is.
 *
 * Empty unless there is exactly one point AND a usable radius. Not "the first point of
 * whatever is drawn": an orbit stores exactly one waypoint, so more than one means the panel
 * and the map disagree about what is being planned, and drawing a ring around point one would
 * hide that rather than show it.
 */
function buildPlanOrbitFeatures(
  planWaypoints: PlanRoutePoint[],
  planOrbitRadiusM: number | null | undefined,
): GeoJSON.Feature[] {
  if (!planOrbitRadiusM || planOrbitRadiusM <= 0) return [];
  if (planWaypoints.length !== 1) return [];
  const centre = planWaypoints[0];
  return [{
    type: 'Feature',
    properties: { radius_m: planOrbitRadiusM },
    geometry: {
      type: 'Polygon',
      coordinates: [circleRingCoords(centre.lat, centre.lng, planOrbitRadiusM)],
    },
  }];
}
